import React, {useEffect, useRef, useState} from 'react';
import classnames from 'classnames';
import styles from './ChipInputField.module.css';
import {ValueValidator} from '../validator/Validator';

interface ChipInputFieldProps {
    isRequired?: boolean;
    title: string;
    value?: string; // string of ; separated values
    setValue: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    errorMessage?: string;
    chipValidator?: ValueValidator<any>
}

interface ChipValue {
    value: string;
    isValid: boolean;
    validating?: boolean;
}

export const ChipInputField: React.FC<ChipInputFieldProps> = ({
                                                                  isRequired = true,
                                                                  title,
                                                                  value = '',
                                                                  setValue,
                                                                  placeholder = "Type value and press Enter",
                                                                  disabled = false,
                                                                  errorMessage,
                                                                  chipValidator
                                                              }) => {
    const [inputValue, setInputValue] = useState('');
    const [chips, setChips] = useState<ChipValue[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    // parse value → chips whenever external value changes
    useEffect(() => {
        const parsed = !value
            ? []
            : value.split(';')
                .map(v => v.trim())
                .filter(v => v.length > 0)
                .map(v => ({value: v, isValid: true}));
        setChips(parsed);
    }, [value]);

    // validate chips when they change
    useEffect(() => {
        if (!chipValidator) return;

        chips.forEach((chip, idx) => {
            const maybePromise = chipValidator('value', chip.value);

            if (maybePromise instanceof Promise) {
                setChips(prev =>
                    prev.map((c, i) =>
                        i === idx ? {...c, validating: true} : c
                    )
                );
                maybePromise.then(outcome => {
                    setChips(prev =>
                        prev.map((c, i) =>
                            i === idx ? {...c, isValid: outcome.valid, validating: false} : c
                        )
                    );
                });
            } else {
                setChips(prev =>
                    prev.map((c, i) =>
                        i === idx ? {...c, isValid: maybePromise.valid} : c
                    )
                );
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chips.length, chipValidator]);

    const updateValue = (newChips: ChipValue[]) => {
        setChips(newChips);
        const newValue = newChips.map(chip => chip.value).join(';');
        setValue(newValue);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        if (['Enter', ';', ',', ' '].includes(e.key)) {
            e.preventDefault();
            addChip();
        } else if (e.key === 'Backspace' && inputValue === '' && chips.length > 0) {
            removeChip(chips.length - 1);
        }
    };

    const addChip = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue === '') return;

        const valueExists = chips.some(chip => chip.value.toLowerCase() === trimmedValue.toLowerCase());
        if (valueExists) {
            setInputValue('');
            return;
        }

        const newChip: ChipValue = {value: trimmedValue, isValid: true};
        updateValue([...chips, newChip]);
        setInputValue('');
    };

    const removeChip = (index: number) => {
        if (disabled) return;
        const newChips = chips.filter((_, i) => i !== index);
        updateValue(newChips);
    };

    const handleInputBlur = () => {
        if (inputValue.trim() !== '') {
            addChip();
        }
    };

    const handleContainerClick = () => {
        if (!disabled && inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.titleWrapper}>
                <span className={classnames(styles.title, disabled && styles.disabledTitle)}>
                    {title}
                </span>
                {(isRequired && !disabled) && <span className={styles.asterisk}>*</span>}
            </div>
            <div className={styles.inputContainer}>
                <div
                    className={classnames(
                        errorMessage ? styles.chipContainerWithError : styles.chipContainer,
                        disabled && styles.chipContainerDisabled
                    )}
                    onClick={handleContainerClick}
                >
                    {chips.map((chip, index) => (
                        <div
                            key={`${chip.value}-${index}`}
                            className={
                                chip.validating
                                    ? styles.chipWrapperValidating
                                    : chip.isValid
                                        ? styles.chipWrapper
                                        : styles.chipWrapperInvalid
                            }
                        >
                            <span className={styles.chipText} title={chip.value}>
                                {chip.value}
                            </span>
                            <button
                                type="button"
                                className={classnames(
                                    styles.deleteButton,
                                    disabled && styles.deleteButtonDisabled
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeChip(index);
                                }}
                                disabled={disabled}
                                aria-label={`Remove ${chip.value}`}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleInputBlur}
                        placeholder={chips.length === 0 ? placeholder : ''}
                        disabled={disabled}
                        className={classnames(
                            styles.input,
                            disabled && styles.inputDisabled
                        )}
                    />
                </div>
            </div>
            <div style={{visibility: !errorMessage ? "hidden" : "visible"}} className={styles.errorWrapper}>
                <img src={"/alertIcon.svg"} alt={"alert icon"}/>
                <span className={styles.errorMessage}>{errorMessage}</span>
            </div>
        </div>
    );
};
